import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-bd3daa5c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "toggle-container" }

import { computed, ref, inject, watch, PropType } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import GridBase from "@/components/ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import { DxToolbarItem } from "devextreme-vue/popup";
import { displayQuestionAlert } from "@/alerts/questionAlert";
import SwitchToggle from "@/components/ui/toggles/switch-toggle.vue";
import { useUserPermissionsStore } from "@/stores/UserPermissionsStore";
import { UserPermissions } from "@/types/user/permissions/UserPermissions";
import NewEditEmailTemplatesModal from "@/components/email/new-edit-email-templates-modal.vue";
import { displayCancelConfirmationAlert } from "@/alerts/cancelConfirmationAlert";
import { BasicMarketingPreferences } from "@/types/contacts/BasicMarketingPreferences";
import { TemplateContentTypes } from "@/enums/TemplateContentTypes";
import { Emails } from "acquaint-api/models/Emails";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { MarketingPreferences } from "@/types/MarketingPreferences";


export default /*#__PURE__*/_defineComponent({
  __name: 'email-templates-configuration-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },

  /**
   * Indicates whether the component is accessed from the configuration screen.
   * @type {boolean}
   * @required
   */
  accessedFromConfigurationScreen: {
    type: Boolean,
    required: true,
  },

  /**
   * The contact's marketing preferences, which can be either basic or detailed.
   * @type {BasicMarketingPreferences | MarketingPreferences}
   * @default {} (empty object)
   */
  contactPermissions: {
    type: Object as PropType<BasicMarketingPreferences | MarketingPreferences>,
    default: undefined,
  },

  /**
   * A list of items to display, typically represented as ID-description pairs.
   * @type {IDDescriptionPair[]}
   * @default [] (empty array)
   */
  listToDisplay: {
    type: Array as PropType<IDDescriptionPair[]>,
    default: () => [],
  },

  /**
   * Determines whether the template is intended for bulk emailing (en masse).
   * @type {boolean}
   * @default false
   */
  templateForEnMasseEmailing: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the template is intended for appointment type.
   * @type {boolean}
   * @default false
   */
  templateForAppointmentType: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted when a template is selected.
   * @property {number} selectedConfigurationItem.id - The id of the selected template.
   * @property {boolean} selectedConfigurationItem.contentTypeID - The contentTypeID of the selected template is TemplateContentTypes.Marketing or not.
   */
  "configurationSelected",
],
  setup(__props, { emit }) {

const props = __props;




const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const userPermissionsStore = useUserPermissionsStore();
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const emailTemplateList = ref<IDDescriptionPair[]>([]);
const showInactiveItems = ref(false);
const userPermissions = ref<UserPermissions>();
const editNewModalId = ref(0);
const selectedConfigurationItem = ref<IDDescriptionPair>({
  id: 0,
  description: "",
});
const displayEditNewModal = ref(false);
const templateData = ref<Emails>();

const disableEdit = computed(() => {
  return selectedConfigurationItem.value.id == 0;
});

const showInactive = ref(0);

const fetchConfigurationList = async () => {
  try {
    emailTemplateList.value = await acquaintApiClient.httpGet<
      IDDescriptionPair[]
    >(
      `app/system/lookup/${LookupTableSource.EmailTemplates}?includeinactive=${showInactiveItems.value}`,
    );
  } catch (err) {
    displayErrorMessage("The selected details could not be fetched");
    emailTemplateList.value = [];
  }
};

watch(
  () => showModal.value,
  async () => {
    selectedConfigurationItem.value = {
      id: 0,
      description: "",
    };
    if (showModal.value) {
      userPermissions.value = await userPermissionsStore.getUserPermissions();
    }

    if (props.listToDisplay.length == 0) {
      await fetchConfigurationList();
    } else {
      emailTemplateList.value = props.listToDisplay;
    }
  },
);

const configurationItemSelected = (e: any) => {
  if (!e) {
    return;
  }
  selectedConfigurationItem.value = e;
};

const configurationItemDoubleClicked = async (e: any) => {
  if (!e) {
    return;
  }
  if (!props.accessedFromConfigurationScreen) {
    if (!(await checkEmailMarketingPreference())) {
      return;
    }

    if (props.templateForEnMasseEmailing) {
      await fetchTemplateData();
    }
    emit(
      "configurationSelected",
      e.id,
      templateData.value?.contentTypeID == TemplateContentTypes.Marketing,
      e.description,
    );
    showModal.value = false;
    return;
  }

  selectedConfigurationItem.value = e;
  editNewModalId.value = selectedConfigurationItem.value.id;
  displayEditNewModal.value = true;
};

const onSelectButtonClicked = async () => {
  if (selectedConfigurationItem.value.id > 0) {
    if (props.accessedFromConfigurationScreen) {
      editNewModalId.value = selectedConfigurationItem.value.id;
      displayEditNewModal.value = true;
      return;
    } else {
      if (props.templateForEnMasseEmailing) {
        await fetchTemplateData();
      }

      emit(
        "configurationSelected",
        selectedConfigurationItem.value.id,
        templateData.value?.contentTypeID == TemplateContentTypes.Marketing,
        selectedConfigurationItem.value.description,
      );
      showModal.value = false;
    }
  }
};

const openEditModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.systemConfiguration.edit
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to edit these details.",
    );
    return;
  }

  editNewModalId.value = selectedConfigurationItem.value.id;
  displayEditNewModal.value = true;
};

const openNewModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.systemConfiguration.edit
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to create a new system item.",
    );
    return;
  }

  editNewModalId.value = 0;
  displayEditNewModal.value = true;
};

const deleteConfigurationItem = async () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.systemConfiguration.edit
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to delete these details.",
    );
    return;
  }

  if (selectedConfigurationItem.value.id < 1000) {
    displayErrorMessage(
      "Sorry, you can't delete this system item as it is required by Acquaint. However you may edit it, and set it as Inactive to remove it from this list.",
    );
    return;
  }

  const question = await displayQuestionAlert(
    "Are you sure you want to delete these details?",
  );

  if (!question.isConfirmed) {
    return;
  }

  if (await callDeletApi()) {
    selectedConfigurationItem.value = { id: 0, description: "" };
    await fetchConfigurationList();
  }
};

const callDeletApi = async () => {
  try {
    await acquaintApiClient.httpDeleteVoid(
      `database/emails/${selectedConfigurationItem.value.id}`,
    );
    return true;
  } catch (err) {
    displayErrorMessage(
      "Sorry, these details cannot be deleted as they are referenced elsewhere.",
    );
    return false;
  }
};

const inactiveToggleValueChange = async () => {
  showInactiveItems.value = showInactive.value ? true : false;
  await fetchConfigurationList();

  if (showInactive.value) {
    return;
  }

  const findInactiveItem = emailTemplateList.value?.find((item) => {
    return item.id == selectedConfigurationItem.value.id;
  });

  if (!findInactiveItem) {
    selectedConfigurationItem.value = { id: 0, description: "" };
  }
};

const newItemCreated = async () => {
  selectedConfigurationItem.value = { id: 0, description: "" };
  await fetchConfigurationList();
};

const editItem = async (inActive: number) => {
  if (inActive && !showInactiveItems.value) {
    selectedConfigurationItem.value = { id: 0, description: "" };
  }

  await fetchConfigurationList();
};

const checkEmailMarketingPreference = async () => {
  if (props.contactPermissions && !props.contactPermissions.email) {
    await fetchTemplateData();
    // If the API call was unsuccessful or the selected template isn't marketing material, there is no need to display the alert.
    if (
      !templateData.value ||
      templateData.value.contentTypeID != TemplateContentTypes.Marketing
    ) {
      return true;
    }

    if (props.templateForAppointmentType) return true;

    const alert = await displayCancelConfirmationAlert(
      "This Contact has not subscribed to receive Marketing correspondence from you. If you continue you may be breaching regulations. Are you sure you want to continue?",
    );
    return alert.isConfirmed;
  } else {
    return true;
  }
};

const fetchTemplateData = async () => {
  try {
    templateData.value = await acquaintApiClient.httpGet<Emails>(
      `database/emails/${selectedConfigurationItem.value.id}`,
    );
  } catch (err) {
    templateData.value = undefined;
    displayErrorMessage(getErrorMessage(err));
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      title: "Email Templates",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      "show-close-button": true,
      height: "80vh"
    }, {
      "non-scrollable-content": _withCtx(() => [
        _createVNode(GridBase, {
          dataSource: emailTemplateList.value,
          onRowClick: configurationItemDoubleClicked,
          onRowSelected: configurationItemSelected,
          "id-column-name": "id",
          showColumnHeaders: false,
          "pre-selected-id": selectedConfigurationItem.value.id
        }, {
          "header-content": _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(SwitchToggle, {
                label: "Show Inactive",
                modelValue: showInactive.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showInactive).value = $event)),
                readOnly: false,
                onOnVaueChange: inactiveToggleValueChange,
                "margin-bottom": "0rem"
              }, null, 8, ["modelValue"])
            ])
          ]),
          columns: _withCtx(() => [
            _createVNode(_unref(DxColumn), {
              "data-field": "description",
              caption: "",
              "hiding-priority": 8,
              width: "100%"
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "id",
              caption: "Id",
              "hiding-priority": 1
            })
          ]),
          _: 1
        }, 8, ["dataSource", "pre-selected-id"])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Select',
          visible: true,
          disabled: disableEdit.value,
          onClick: () => {
            onSelectButtonClicked();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Delete',
          visible: props.accessedFromConfigurationScreen,
          disabled: disableEdit.value,
          onClick: () => {
            deleteConfigurationItem();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Edit',
          visible: props.accessedFromConfigurationScreen,
          disabled: disableEdit.value,
          onClick: () => {
            openEditModal();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'New',
          visible: props.accessedFromConfigurationScreen,
          onClick: () => {
            openNewModal();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            showModal.value = false;
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(NewEditEmailTemplatesModal, {
      modelValue: displayEditNewModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((displayEditNewModal).value = $event)),
      configurationId: editNewModalId.value,
      onConfigurationItemCreated: newItemCreated,
      onConfigurationItemEdited: editItem
    }, null, 8, ["modelValue", "configurationId"])
  ], 64))
}
}

})